import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { map } from 'rxjs/operators';
import { NavListCommonProps } from 'src/app/components/nav/nav.component';
import { DialogAddCompositionComponent } from 'src/app/components/shared/dialog-add-composition/dialog-add-composition.component';
import { DialogSelectEuShareComponent } from 'src/app/components/shared/dialog-select-eu-share/dialog-select-eu-share.component';
import { environment } from 'src/environments/environment';
import { GroupBy } from '../_enums/groupby';
import { ProductFamilyType } from '../_enums/product.family.type';
import { ResourceTypeCodes } from '../_enums/resource.type.codes';
import { SiteMapUrl } from '../_enums/sitemap.url';
import { SnackbarTypes } from '../_enums/snackbar.types';
import { AdherentModel } from '../_models/adherent';
import { CombustibleFormula } from '../_models/combustibleFormula';
import { Family } from '../_models/family';
import { Formula } from '../_models/formula';
import { FormulaComposition } from '../_models/formulaComposition';
import { Industrie } from '../_models/industrie';
import { Product } from '../_models/product';
import { ProjectType } from '../_models/projectType';
import { Unity } from '../_models/unity';
import { UserEntity } from '../_models/userEntity';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from './auth.service';
import { FamilyService } from './family.service';
import { IndustrieService } from './industrie.service';
import { ProjectTypeService } from './projectTypes.service';
import { SnackbarService } from './snackbar.service';
import { SpinnerOverlayService } from './spinner.overlay.service';
import { FunctionalityCode } from '../_enums/functionality.code';

@Injectable({
  providedIn: 'root'
})
export class ProductFormulaService {
  families: Array<Family> = [];
  projectTypes: Array<ProjectType> = [];
  unitys: Array<Unity> = [];
  industries: Array<Industrie> = [];
  bondingLayerProducts: Array<Product> = [];

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private router: Router,
    private dialog: MatDialog,
    private spinnerService: SpinnerOverlayService,
    private snackbarService: SnackbarService,
    private familyService: FamilyService,
    private projectTypeService: ProjectTypeService,
    private industrieService: IndustrieService,
    private translate: TranslateService
  ) {
    this.familyService.getRessourceByTypeCode(ResourceTypeCodes.Produit).subscribe((f: any) => {
      if (f.length > 0) {
        this.families = f;
      }
    });

    this.projectTypeService.getAll().subscribe((projectTypes) => {
      this.projectTypes = projectTypes;
    });

    this.getUnity().subscribe((u: any) => {
      if (u.length > 0) {
        this.unitys = u;
      }
    });

    this.industrieService.getIndustriesFormula().subscribe((i: any) => {
      if (i.length > 0) {
        this.industries = i;
      }
    });
  }

  get(resourceId: string, versionId?: string) {
    let params = new HttpParams();
    params = versionId ? params.set('versionId', versionId) : params;
    return this.http.get<any>(`${environment.apiUrl}/ProductFormulas/${resourceId}`, { params: params, headers: this.authService.headers });
  }

  getAll(groupBy?: string, operationType?: string, versionId?: string) {
    let params = new HttpParams();
    params = groupBy ? params.set('groupBy', groupBy) : params;
    params = operationType ? params.set('operationType', operationType) : params;

    params = versionId ? params.set('versionId', versionId) : params;

    return this.http.get<Product[]>(`${environment.apiUrl}/ProductFormulas`, {
      params: params,
      headers: this.authService.headers
    });
  }

  getFormulas(groupBy?: string) {
    let params = new HttpParams();
    params = groupBy ? params.set('groupBy', groupBy) : params;

    return this.http.get<any[]>(`${environment.apiUrl}/ProductFormulas/formulas`, { params: params, headers: this.authService.headers });
  }

  getFormulaComposition(formulaId: string) {
    return this.http.get<FormulaComposition[]>(`${environment.apiUrl}/ProductFormulas/${formulaId}/formulaComposition`, { headers: this.authService.headers });
  }

  updateProduct(product: Product) {
    return this.http.put<Product>(`${environment.apiUrl}/ProductFormulas/${product.resourceId}`, product, { headers: this.authService.headers });
  }

  updateFormula(formula: Formula, versionId?: string) {
    let params = new HttpParams();
    params = versionId ? params.set('versionId', versionId) : params;
    return this.http.put<Formula>(`${environment.apiUrl}/ProductFormulas/formula/${formula.resourceId}`, formula, { params: params, headers: this.authService.headers });
  }

  addProduct(product: Product) {
    return this.http.post<Product>(`${environment.apiUrl}/ProductFormulas/produit`, product, { headers: this.authService.headers });
  }

  addFormula(formula: Formula, versionId?: string) {
    let params = new HttpParams();
    params = versionId ? params.set('versionId', versionId) : params;
    return this.http.post<Formula>(`${environment.apiUrl}/ProductFormulas/formule`, formula, { params: params, headers: this.authService.headers });
  }

  delete(resourceId: string) {
    return this.http.delete(`${environment.apiUrl}/ProductFormulas/${resourceId}`, { headers: this.authService.headers });
  }

  deleteMany(resourceIds: string[]) {
    return this.http.delete<{ results: { itemId?: string; resourceId?: string; id?: string; success: boolean }[] }>(`${environment.apiUrl}/ProductFormulas/deleteBulk`, {
      headers: this.authService.headers,
      body: { itemIds: resourceIds }
    });
  }

  unlink(resourceId: string) {
    return this.http.post(`${environment.apiUrl}/ProductFormulas/unpublish/${resourceId}/${this.authService.roleValue?.perimeterId}`, null, { headers: this.authService.headers });
  }

  duplicate(resourceId: string, code: string) {
    return this.http.post(`${environment.apiUrl}/ProductFormulas/duplicate?id=${resourceId}&code=${encodeURIComponent(code)}`, null, { headers: this.authService.headers });
  }

  publishFormula(resourceId: string, eus?: UserEntity[]) {
    return this.http.post<Formula>(`${environment.apiUrl}/ProductFormulas/publish/${resourceId}`, eus, { headers: this.authService.headers });
  }

  getUnity() {
    return this.http.get<any[]>(`${environment.apiUrl}/ProductFormulas/Unity`, {
      headers: this.authService.headers
    });
  }

  getEUsToShareFormula(resourceId: string) {
    return this.http.get<AdherentModel[]>(`${environment.apiUrl}/ProductFormulas/EusToShare/${resourceId}`, { headers: this.authService.headers });
  }

  getCombustibles(formulaId?: string, industryId?: string) {
    return this.http.get<CombustibleFormula[]>(`${environment.apiUrl}/ProductFormulas/combustibles/?formulaId=${formulaId}&industryId=${industryId}`, { headers: this.authService.headers });
  }

  updateFormulaComposition(resourceCompositionId: string, productId: string) {
    return this.http.put<void>(`${environment.apiUrl}/ProductFormulas/updateFormulaComposition/${resourceCompositionId}/${productId}`, null, { headers: this.authService.headers });
  }

  private onClickNewItem(type: string, perimeterId: string) {
    this.router.navigate([type == 'formula' ? `${SiteMapUrl.MenuProductFormula}/new/f` : `${SiteMapUrl.MenuProductFormula}/new/p`, 0], {
      state: {
        families: type == 'formula' ? this.getFormulaFamilies() : this.families,
        projectTypes: this.projectTypes,
        unitys: this.unitys,
        industries: this.industries,
        bondingLayerProducts: this.bondingLayerProducts,
        perimeterId,
        isComplexProduct: type == 'complexProduct'
      }
    });
  }

  private shareFormulas() {
    this.getFormulas().subscribe((data) => {
      let favoriteData: any[] = [];

      let filteredParents = data.filter(
        (family) => family.value!.some((formula: { isFavorite: any }) => formula.isFavorite) || family.value.some((industry: { value: { isFavorite: any }[] }) => industry.value.some((formula: { isFavorite: any }) => formula.isFavorite))
      );

      filteredParents.forEach((fP: { value: any[]; key: any }) => {
        let filteredChildrenValue: any[] = [];

        let filteredChildren = fP.value.filter((member: { key: any; value: { isFavorite: any }[] }) => member.value.some((formula: { isFavorite: any }) => formula.isFavorite));

        filteredChildren.forEach((fC: { value: any[]; key: any }) => {
          let filteredChildrenNext = fC.value.filter((formula: { isFavorite: any }) => formula.isFavorite);

          let itemChildren = {
            key: fC.key,
            value: filteredChildrenNext
          };

          filteredChildrenValue.push(itemChildren);
        });

        let itemParent = {
          key: fP.key,
          value: filteredChildrenValue
        };

        favoriteData.push(itemParent);
      });

      const dialogRef = this.dialog.open(DialogAddCompositionComponent, {
        data: {
          fullTree: data,
          favoriteData: favoriteData
        }
      });

      dialogRef.afterClosed().subscribe(async (selectedFormulas) => {
        if (!selectedFormulas || selectedFormulas.length === 0) return;

        this.spinnerService.show();

        this.getEUsToShareFormula(selectedFormulas[0].resourceId).subscribe((eusData) => {
          this.spinnerService.hide();

          const dialogEu = this.dialog.open(DialogSelectEuShareComponent, {
            data: eusData
          });

          dialogEu.afterClosed().subscribe(async (selectedEus) => {
            if (!selectedEus || selectedEus.length === 0) return;

            const eusToShare: UserEntity[] = selectedEus.map((eu: any) => ({
              displayName: eu.displayName,
              euId: eu.resourceId
            }));

            this.spinnerService.show();

            const publishPromises = selectedFormulas.map((formula: { resourceId: string }) => this.publishFormula(formula.resourceId, eusToShare).toPromise());

            await Promise.all(publishPromises);
            this.spinnerService.hide();
            this.snackbarService.open(SnackbarTypes.share);
          });
        });
      });
    });
  }

  private getActions(perimeterId: string) {
    const manageProducts = this.authService.accessesValues.includes(FunctionalityCode.ManageProducts);
    const manageProductsEcoPro = this.authService.accessesValues.includes(FunctionalityCode.ManageProductEcoPro);
    if (!manageProducts) return [];
    const actions = [{ label: 'ressource_list_action_create_product', onClick: () => this.onClickNewItem('product', perimeterId) }];

    if (manageProductsEcoPro) {
      actions.push({ label: 'ressource_list_action_create_complex_product', onClick: () => this.onClickNewItem('complexProduct', perimeterId) });
    }

    actions.push({ label: 'ressource_list_action_create_formula', onClick: () => this.onClickNewItem('formula', perimeterId) });
    actions.push({ label: 'ressource_list_action_share_formulas', onClick: () => this.shareFormulas() });

    return actions;
  }

  private getFormulaFamilies() {
    return this.families.filter((x) => x.modality != ProductFamilyType.Other.toString());
  }

  getNavConfig({ perimeterId, dataSource, updateDataSource, navComponent }: NavListCommonProps) {
    return {
      header: {
        title: 'ressource_list_title_products',
        actions: this.getActions(perimeterId),
        filters: [
          { label: 'ressource_list_filter_by_family', value: GroupBy.family },
          { label: 'ressource_list_filter_by_favorite', value: GroupBy.favorite }
        ],
        defaultFilter: GroupBy.family
      },
      getItemConfig: (node: any) => {
        return {
          displayName: `${node.displayName} [${node.code}]`,
          url: `${SiteMapUrl.MenuProductFormula}/${node.detailedResource ? 'f' : 'p'}/${node.resourceId}`,
          state: {
            families: node.detailedResource ? this.getFormulaFamilies() : this.families,
            projectTypes: this.projectTypes,
            unitys: this.unitys,
            industries: this.industries,
            bondingLayerProducts: this.bondingLayerProducts,
            resourceId: node.resourceId,
            perimeterId
          },
          itemType: node.detailedResource ? 'formula' : 'product'
        };
      },
      getDataSource: (groupBy?: GroupBy) =>
        this.getAll(groupBy).pipe(
          map((data: any[]) =>
            data.map((node: any) => ({
              key: node.key,
              type: 1,
              value:
                node.value && node.value?.length == 1 && node.value[0]?.key == node.key
                  ? node.value?.[0]?.value
                  : node.value.map((child: any) => ({
                      key: child.key,
                      displayName: child.key,
                      value: child.value?.filter((c: any) => c.displayName),
                      type: 2
                    }))
            }))
          )
        ),
      getEditableFamilyId: (node: any) => node.value?.[0]?.familyId ?? node.value?.[0]?.value?.[0]?.familyId,
      addFamilyType: ResourceTypeCodes.Produit,
      onDelete: (selectedItems: any[]) => this.onDeleteProducts(selectedItems, dataSource, updateDataSource, navComponent)
    };
  }

  private onDeleteProducts(selectedItems: any[], dataSource: MatTreeNestedDataSource<any>, updateDataSource: (newDataSource: any[]) => void, navComponent: any) {
    return navComponent.createDeleteHandler({
      deleteMany: (ids: string[]) => this.deleteMany(ids),
      getItemId: (item: any) => item?.resourceId || item?.id || item?.key,
      filterData: (data: any[], deletedIds: string[]) => this.filterNodes(data, deletedIds),
      successMessage: (count: number) => this.translate.instant(count > 1 ? 'success_resources_deleted' : 'success_resource_deleted'),
      errorMessage: 'error_no_item_selected',
      snackbarService: this.snackbarService
    })(selectedItems, dataSource, updateDataSource);
  }

  private filterNodes(nodes: any[], ids: string[]): any[] {
    if (!Array.isArray(nodes) || !nodes.length) {
      return [];
    }
    const filtered: any[] = [];
    nodes.forEach((node) => {
      if (!node) {
        return;
      }
      const nodeId = node?.resourceId || node?.id || node?.key;
      if (nodeId && ids.includes(nodeId)) {
        return;
      }
      if (Array.isArray(node.value)) {
        const nextChildren = this.filterNodes(node.value, ids);
        if (!nextChildren.length && !nodeId) {
          return;
        }
        filtered.push({ ...node, value: nextChildren });
        return;
      }
      filtered.push(node);
    });
    return filtered;
  }
}
